import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone, Mail, ChevronRight, Sparkles, Award, TrendingUp, Calendar, MapPin, Zap } from "lucide-react";
import { BRAND } from "@/config/brand";
import PlugitAIChat from "@/pages/PlugitAIChat";
import KitTicker from "@/pages/KitTicker";
import KitOnTV from "@/pages/KitOnTV";
import HowWeWork from "@/pages/HowWeWork";
import MobilePocketPopup from "@/pages/MobilePocketPopup";

const C = BRAND.colors;
const teal = "#14B8A6";
const gold = "#FFD700";

const TIMELINE = [
  { year: "19", label: "Broke into the auto industry.",
    body: "Most people were figuring out college. I was already on the sales floor learning how customers actually decide — what earns their trust, what breaks it, and how the phone rings turn into deals." },
  { year: "27", label: "Youngest GM at Central Kia.",
    body: "I ran a full dealership before most people my age were even senior salespeople. Sales, service, finance, hiring, culture — all of it. If it happened inside that showroom, my name was on it." },
  { year: "2017", label: "Google-certified.",
    body: "Google put me on their expert list back in 2017. I never made a big deal about it — I just used it to help the stores I was running and, over time, the small businesses I liked working with." },
  { year: "2021", label: "Retired from the auto game.",
    body: "Twenty years of showroom life. Every kind of customer, every kind of deal, every kind of team. I earned my out. But I couldn't sit still." },
  { year: "2022", label: "Opened my first web-design business.",
    body: "One site turned into two. Two into ten. Every business owner I met had the same pain: their website didn't work for them. I fixed that for them, one build at a time — and I never looked back." },
  { year: "4", label: "The team, kept small on purpose.",
    body: "It's four of us — me, a designer, a developer, and a copywriter. Small, senior, all in-house. No overseas subcontracting. That's how we keep quality high and pricing sane." },
  { year: "Now", label: "PlugitAI, powered by NARIai.",
    body: "Websites are the front door of every small business. AI is now the receptionist, the sales rep, and the follow-up manager — 24/7. We build honest, working sites for owners who don't want to be held hostage." },
];

const CREDS = [
  { icon: Sparkles,   label: "Google-certified · 2017",     detail: "Not on the invoice" },
  { icon: TrendingUp, label: "SEO as a hobby",              detail: "Not a bill I'm running up" },
  { icon: Award,      label: "Team of 4 · in-house",         detail: "Senior, no subcontracting" },
  { icon: Calendar,   label: "20 years in auto sales",       detail: "1999 – 2021" },
];

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function upsertCanonical(href) {
  let el = document.querySelector("link[rel='canonical']");
  if (!el) { el = document.createElement("link"); el.rel = "canonical"; document.head.appendChild(el); }
  el.href = href;
}
function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

export default function AboutKit() {
  useEffect(() => {
    const origin = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    document.title = "About Kit — Founder, PlugitAI | Web Design + AI Sales for Small Businesses";
    upsertMeta("description",
      "Kit (Chris Garcia) and a small in-house team of four build honest, working websites for small business owners. Google-certified since 2017, 20+ years in auto sales, youngest GM at Central Kia at 27. SEO is a hobby — not a bill we're running up. Websites $600–$2,000 with 100 pages of exclusive content and payment options.");
    upsertMeta("robots", "index,follow");
    upsertCanonical(`${origin}/about`);
    upsertJsonLd("ld-person", {
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Kit",
      alternateName: "Chris Garcia",
      jobTitle: "Founder, PlugitAI",
      email: "cgarciathanksyou@gmail.com",
      telephone: "+12148155966",
      description: "Founder and lead designer at PlugitAI. In-house team of 4. Google-certified since 2017. Former youngest General Manager at Central Kia at 27. 20+ years in small-business sales.",
      worksFor: { "@type": "Organization", name: "PlugitAI", url: origin },
    });
    return () => document.getElementById("ld-person")?.remove();
  }, []);

  return (
    <div className="min-h-screen w-full pb-24 md:pb-0"
         style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
         data-testid="about-kit-page">
      <TopNav />
      <KitTicker />

      {/* Ambient bg */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0"
           style={{
             background: `radial-gradient(1200px 700px at 15% -10%, ${C.blueBright}22, transparent 60%),
                          radial-gradient(900px 600px at 90% 15%, ${teal}18, transparent 60%),
                          radial-gradient(1400px 900px at 60% 110%, ${gold}0e, transparent 70%)`,
           }} />

      {/* Hero */}
      <section className="relative z-10 pt-28 md:pt-40 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 mb-8" style={{ color: C.textFade }}>
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <span style={{ color: C.text }}>About Kit</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.24em] mb-8"
               style={{ background: `${teal}18`, color: teal, border: `1px solid ${teal}66` }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: teal, boxShadow: `0 0 8px ${teal}` }} />
            Small team · big work
          </div>

          <h1 className="font-black tracking-[-0.02em] leading-[0.98]"
              style={{ fontSize: "clamp(2.4rem, 5.4vw, 5.4rem)", color: "white" }}
              data-testid="about-h1">
            An honest team of four{" "}
            <span style={{
              backgroundImage: `linear-gradient(135deg, ${C.blueGlow} 0%, ${teal} 55%, ${gold} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: `drop-shadow(0 0 24px ${teal}55)`,
            }}>
              building websites that actually work
            </span>
            .
          </h1>

          {/* Hero split — portrait + intro */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-[280px,1fr] gap-8 md:gap-10 items-center">
            <div className="relative mx-auto md:mx-0 w-56 h-56 md:w-[280px] md:h-[340px]" data-testid="about-portrait">
              <div
                aria-hidden
                className="absolute -inset-3 rounded-3xl"
                style={{
                  background: `conic-gradient(from 130deg, ${C.blueBright}, ${teal}, ${gold}, ${C.blueBright})`,
                  filter: "blur(18px)",
                  opacity: 0.5,
                }}
              />
              <div
                className="relative w-full h-full rounded-3xl overflow-hidden"
                style={{
                  border: `2px solid ${teal}88`,
                  boxShadow: `0 40px 100px -30px ${teal}77, inset 0 0 0 3px rgba(0,0,0,0.35)`,
                }}
              >
                <img
                  src="/founder-chris.png"
                  alt="Kit — Chris Garcia, founder of PlugitAI"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "50% 22%" }}
                />
              </div>
              <div
                className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.2em] whitespace-nowrap"
                style={{ background: C.ink, color: gold, border: `1px solid ${gold}66` }}
              >
                Kit · Founder · 45
              </div>
            </div>

            <p className="max-w-[640px] text-lg md:text-xl leading-relaxed" style={{ color: C.textDim }}>
              I&apos;m <span className="text-white font-semibold">Kit</span> — 45, born and raised in DFW, 27 years in small-business sales. There are four of us: me, a designer, a developer, and a copywriter. All in-house. SEO is my hobby, so <span className="text-white font-semibold">it never shows up on your invoice as a padded line-item</span>. Tell us what you&apos;ve got going on and we&apos;ll tell you exactly how we&apos;d rank it.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="tel:+12148155966"
               className="inline-flex items-center gap-2 px-7 h-14 rounded-full font-semibold text-base transition-all hover:-translate-y-0.5"
               style={{
                 background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`,
                 color: "white",
                 boxShadow: `0 24px 60px -20px ${teal}aa`,
               }}
               data-testid="about-cta-call">
              Talk to me — (214) 815-5966 <ArrowRight size={16} />
            </a>
            <a href="mailto:cgarciathanksyou@gmail.com?subject=Design%20my%20future%20with%20PlugitAI"
               className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border transition-all hover:bg-white/5"
               style={{ borderColor: `${teal}66`, color: "white" }}
               data-testid="about-cta-email">
              Email me directly <ArrowUpRight size={16} />
            </a>
          </div>

          {/* Credential chips */}
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {CREDS.map((c) => (
              <div key={c.label}
                   className="rounded-2xl p-4 border"
                   style={{ borderColor: C.line, background: "rgba(17,24,39,0.55)", backdropFilter: "blur(10px)" }}
                   data-testid={`about-cred-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <c.icon size={18} style={{ color: teal }} />
                <div className="mt-2 font-bold text-white text-sm leading-tight">{c.label}</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.2em]" style={{ color: C.textFade }}>{c.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kit on TV — press reel carousel */}
      <KitOnTV />

      {/* How we actually work — honest description under the images */}
      <HowWeWork />

      {/* The story — personal letter */}
      <section className="relative z-10 py-20 md:py-28 border-y" style={{ borderColor: C.line, background: "rgba(11,21,36,0.72)" }}>
        <div className="mx-auto max-w-[820px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-4" style={{ color: gold }}>Kit&apos;s story · in his own words</div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02] mb-10"
              style={{ fontSize: "clamp(2rem, 3.8vw, 3rem)", color: "white" }}>
            From the showroom floor to <span style={{ color: teal }}>your website</span>.
          </h2>
          <div className="space-y-5 text-base md:text-lg leading-relaxed text-white/85">
            <p>I&apos;m <strong className="text-white">Kit</strong>. I&apos;m <strong className="text-white">45</strong>, born and raised right here in the DFW area. I don&apos;t look like the guy you&apos;d expect to be running an AI-plus-web company — and that&apos;s exactly the point. I don&apos;t come from Silicon Valley. I come from the sales floor.</p>
            <p>I got into the auto industry when I was <strong className="text-white">19</strong>. Most kids my age were figuring out what they wanted to do. I was already on a sales floor learning what actually earns a customer&apos;s trust — and what quietly kills it.</p>
            <p>By <strong className="text-white">27</strong>, I was the <strong style={{ color: teal }}>youngest General Manager at Central Kia</strong>. I ran the store — sales, service, finance, culture, all of it. Twenty years of face-to-face work in a business where every conversation is worth thousands of dollars teaches you something you can&apos;t get from a classroom.</p>
            <p>I retired from the auto game in <strong className="text-white">2021</strong>. I had earned the exit. But I couldn&apos;t sit still.</p>
            <p>Google put me on their expert list back in <strong className="text-white">2017</strong>, and I&apos;ve kept sharpening it ever since. Honest truth: <strong className="text-white">SEO is a hobby for me.</strong> That&apos;s the whole reason it never shows up on your invoice as a padded line-item. Most agencies bill you monthly for it in perpetuity. I just build it in.</p>
            <p>In <strong className="text-white">2022</strong> I opened my first web-design business. One site turned into two. Two into ten. Every owner I met had the same pain: their website didn&apos;t work for them. It just sat there. So we started building sites that actually sell — and I haven&apos;t looked back.</p>
            <p>Today we&apos;re a team of <strong style={{ color: teal }}>four</strong>: me, a designer, a developer, and a copywriter. All in-house. No overseas subcontracting. That&apos;s why we can quote a real figure to rank your site, send you links to work we&apos;ve personally built this year, and never <strong className="text-white">hold your website hostage</strong> after launch.</p>
            <p>Every site we ship comes with <strong style={{ color: gold }}>100 pages of exclusive content</strong>. Show me one other outfit that does that without charging you $3,500 and up. That&apos;s not us. Our average website-design projects range from <strong className="text-white">$600 to $2,000</strong>, and we have payment options if that&apos;s what makes it doable.</p>
            <p style={{ color: teal }} className="text-lg md:text-xl font-semibold">
              PlugitAI is what happens when 20 years of sales-floor instinct meets a small, senior team who actually likes the work.
            </p>
            <p className="text-white font-semibold">
              Tell us what you&apos;ve got going on and we&apos;ll tell you exactly what we&apos;re going to do to make you rank.
            </p>
            <p className="pt-4">— Kit</p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative z-10 py-24 md:py-32" data-testid="about-timeline">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] mb-3 text-center" style={{ color: teal }}>The road here</div>
          <h2 className="font-black tracking-[-0.02em] leading-[1.02] text-center mb-16"
              style={{ fontSize: "clamp(1.9rem, 3.6vw, 3rem)", color: "white" }}>
            27 years of showing up.
          </h2>

          <div className="space-y-6">
            {TIMELINE.map((t, i) => (
              <div key={t.year}
                   className="relative rounded-2xl p-6 md:p-8 border flex flex-col md:flex-row md:items-start gap-6"
                   style={{
                     borderColor: `${teal}33`,
                     background: "rgba(17,24,39,0.55)",
                     backdropFilter: "blur(10px)",
                     boxShadow: `0 30px 80px -40px ${teal}44`,
                   }}
                   data-testid={`about-timeline-${i}`}>
                <div
                  className="shrink-0 w-24 h-24 md:w-32 md:h-32 rounded-2xl flex items-center justify-center font-black text-2xl md:text-3xl"
                  style={{
                    background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`,
                    color: "white",
                    boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.15), 0 20px 60px -20px ${teal}88`,
                  }}
                >
                  {t.year}
                </div>
                <div>
                  <h3 className="font-black text-white text-xl md:text-2xl mb-2">{t.label}</h3>
                  <p className="text-base leading-relaxed" style={{ color: C.textDim }}>{t.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 pb-28 md:pb-36">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="relative rounded-3xl p-8 md:p-14 border overflow-hidden"
               style={{
                 background: `linear-gradient(135deg, ${teal}20 0%, ${gold}12 100%), ${C.inkSoft}`,
                 borderColor: `${teal}55`,
                 boxShadow: `0 80px 200px -60px ${teal}66`,
               }}
               data-testid="about-final-cta">
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: gold }}>
                Start with a real conversation
              </div>
              <h3 className="font-black tracking-[-0.02em] leading-[1.02]"
                  style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.8rem)", color: "white" }}>
                Let me personally design your future.
              </h3>
              <p className="mt-4 max-w-[640px] text-base md:text-lg leading-relaxed text-white/85">
                Lock your project in with <span className="font-black" style={{ color: teal }}>$299.99</span> and I&apos;ll begin designing the website, the AI twin, and the sales system around <em>your</em> business. Not a template. Not a corporate machine. Me and my team, working directly with you.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href="tel:+12148155966"
                   className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base"
                   style={{
                     background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`,
                     color: "white",
                     boxShadow: `0 24px 60px -20px ${teal}aa`,
                   }}>
                  <Phone size={14} /> Call (214) 815-5966 <ArrowRight size={16} />
                </a>
                <button type="button"
                        onClick={() => document.querySelector("[data-testid=plugitai-chat-open]")?.click()}
                        className="inline-flex items-center gap-2 px-6 h-14 rounded-full font-semibold text-base border transition-all hover:bg-white/5"
                        style={{ borderColor: `${teal}77`, color: "white" }}>
                  Chat with Kit&apos;s assistant <Zap size={14} />
                </button>
                <a href="mailto:cgarciathanksyou@gmail.com"
                   className="inline-flex items-center gap-2 px-4 h-11 rounded-full font-semibold text-sm border"
                   style={{ borderColor: C.line, color: C.textDim }}>
                  <Mail size={13} /> Email me
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PlugitAIChat />
      <MobilePocketPopup />
    </div>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto" />
          <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}
